import React from 'react';
import { Tag, Space, Button, Popover, Popconfirm } from 'antd';
import {
  UserOutlined, CalendarOutlined, EyeOutlined, PlusCircleOutlined,
  DeleteOutlined, EditOutlined, ExclamationCircleOutlined, CheckCircleOutlined, WhatsAppOutlined,
  StopOutlined, FlagOutlined,
} from '@ant-design/icons';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { NdoeDataCard, NdoeActionButton, NdoeStatusBadge } from '../../../components/ndoe';

/* ---------------------------------------------------------------------------
   Helpers partagés par ListeCommande, ListeCommandeJour et ListeCommande7jrs
   (évite la triplication des colonnes / cartes / statuts).
--------------------------------------------------------------------------- */

/** Code commande lisible : AA + shop(2) + id(4). */
export const formatCommandeCode = (record) => {
  try {
    return `${new Date().getFullYear().toString().substring(2)}${record.id_shop.toString().padStart(2, '0')}${record.id_commande.toString().padStart(4, '0')}`;
  } catch {
    return record?.id_commande ?? '—';
  }
};

/** Numéro de téléphone du client si présent (champ non garanti selon l'API). */
export const phoneOf = (record) => record?.telephone || record?.tel || record?.numero || record?.phone || null;

/** Statut commande (Validé / Non-validé) → couleur + icône AntD. */
export const StatutTag = ({ text }) => {
  let tagColor = '';
  let icon = null;
  if (text === 'Non-validé') { tagColor = 'red'; icon = <ExclamationCircleOutlined />; }
  else if (text === 'Validé') { tagColor = 'green'; icon = <CheckCircleOutlined />; }
  else if (text === 'Annulée') { tagColor = 'default'; icon = <StopOutlined />; } // clôture staff sans vente (15/07)
  return <Tag color={tagColor}>{icon} {text}</Tag>;
};

/** ② Montant à afficher dans la liste : validé (vente confirmée) sinon attendu
 *  (total des lignes). Renvoie null si le champ est absent (rôle non financier :
 *  le serveur masque montant_* pour les non admin/secrétaire). */
export const montantInfo = (record) => {
  if (record?.montant_attendu === undefined && record?.montant_valide === undefined) return null;
  const attendu = record?.montant_attendu != null ? Number(record.montant_attendu) : null;
  const valide = record?.montant_valide != null ? Number(record.montant_valide) : null;
  if (valide != null && !Number.isNaN(valide)) {
    // « Validé » seulement si le confirmé COUVRE l'attendu ; sinon vente partielle.
    const couvert = attendu != null && !Number.isNaN(attendu) && valide >= attendu;
    return { montant: valide, attendu, confirme: couvert, partiel: !couvert };
  }
  return { montant: Number(record?.montant_attendu || 0), confirme: false, partiel: false };
};
const money = (n) => `${Number(n || 0).toLocaleString('fr-FR')} $`;

/** Tag Montant : « Validé X$ » (vert) / « Partiel X$/Y$ » (ambre) / « Attendu X$ » (ambre). */
export const MontantTag = ({ record }) => {
  const info = montantInfo(record);
  if (!info) return <span style={{ color: '#9aa3b2' }}>—</span>;
  if (info.confirme) return <Tag color="green">Validé · {money(info.montant)}</Tag>;
  if (info.partiel) return <Tag color="orange">Partiel · {money(info.montant)} / {money(info.attendu)}</Tag>;
  return <Tag color="gold">Attendu · {money(info.montant)}</Tag>;
};

/** Statut livraison dérivé (repli sur l'ancien flag id_livraison). */
export const livraisonStatutOf = (statut, record) => {
  const fallback = {
    0: { label: 'En attente', tone: 'default', ndoe: 'neutral' },
    1: { label: 'En cours', tone: 'processing', ndoe: 'gold' },
    2: { label: 'Livré', tone: 'green', ndoe: 'ok' },
  };
  if (statut && statut.label) {
    // tone AntD fourni par l'API ; on dérive un ton ndoe pour les cartes mobile.
    const ndoe = statut.tone === 'green' ? 'ok' : statut.tone === 'processing' ? 'gold' : 'neutral';
    return { ...statut, ndoe: statut.ndoe || ndoe };
  }
  return fallback[record?.id_livraison] || { label: '—', tone: 'default', ndoe: 'neutral' };
};

/**
 * Colonnes de tableau (desktop) communes.
 * opts : { withSearch(dataIndex)=>props, onClient, onInfo, onEdit, onDelete, isAdmin, indexOffset }
 */
export const buildCommandeColumns = (opts = {}) => {
  const { withSearch, onClient, onInfo, onEdit, onDelete, onCloturer, isAdmin, showMontant, indexOffset = 0 } = opts;
  const search = (dataIndex) => (withSearch ? withSearch(dataIndex) : {});

  return [
    { title: '#', dataIndex: 'id', key: 'id', render: (t, r, index) => indexOffset + index + 1, width: '4%' },
    {
      title: 'Code', dataIndex: 'id_commande', key: 'id_commande',
      render: (text, record) => <Tag color="blue">{formatCommandeCode(record)}</Tag>,
    },
    {
      title: 'Client', dataIndex: 'nom', key: 'id_client', ...search('nom'),
      render: (text, record) => (
        <div onClick={() => onClient && onClient(record.id_client)} style={{ cursor: onClient ? 'pointer' : 'default' }}>
          <Tag color="green"><UserOutlined style={{ marginRight: 5 }} />{text}</Tag>
        </div>
      ),
    },
    {
      title: 'Date commande', dataIndex: 'date_commande', key: 'date_commande', ...search('date_commande'),
      render: (text) => <Tag color="blue" icon={<CalendarOutlined />}>{format(new Date(text), 'dd-MM-yyyy')}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'nom_statut', key: 'nom_statut', ...search('nom_statut'),
      render: (text) => <StatutTag text={text} />,
    },
    ...(showMontant ? [{
      title: 'Montant', key: 'montant', align: 'right',
      render: (t, record) => <MontantTag record={record} />,
    }] : []),
    {
      title: 'Livraison', dataIndex: 'livraison_statut', key: 'livraison_statut',
      render: (statut, record) => {
        const s = livraisonStatutOf(statut, record);
        return (
          <Popover title="Voir les détails de cette livraison" trigger="hover">
            <Tag color={s.tone} onClick={() => onInfo && onInfo(record.id_commande)} style={{ cursor: onInfo ? 'pointer' : 'default' }}>
              {s.label}
            </Tag>
          </Popover>
        );
      },
    },
    {
      title: 'Shop', dataIndex: 'id_shop', key: 'id_shop',
      render: (text) => <Tag color="orange">{text === 1 ? 'Shop Ngaliema' : `Shop ${text ?? '—'}`}</Tag>,
    },
    {
      title: 'Action', key: 'action',
      render: (text, record) => (
        <Space size="middle">
          {onEdit && (
            <Popover title="Modifier" trigger="hover">
              <Button icon={<EditOutlined />} style={{ color: 'green' }} onClick={() => onEdit(record.id_commande)} />
            </Popover>
          )}
          <Popover title="Voir la liste de cette commande" trigger="hover">
            <Link to={`/listeDetailView/${record.id_commande}`}>
              <Button icon={<EyeOutlined />} style={{ color: 'blue' }} />
            </Link>
          </Popover>
          <Popover title="Voir des produits" trigger="hover">
            <Link to={`/commandes/${record.id_commande}`}>
              <Button icon={<PlusCircleOutlined />} style={{ color: 'green' }} />
            </Link>
          </Popover>
          {onCloturer && record.nom_statut !== 'Annulée' && (
            <Popover title="Clôturer : solde les paires restantes en retour magasin" trigger="hover">
              <Popconfirm
                title="Clôturer cette commande ?"
                description="Les paires non livrées reviennent au magasin. Avec au moins une vente → « Livrée » ; sans aucune vente → « Annulée »."
                onConfirm={() => onCloturer(record.id_commande)} okText="Clôturer" cancelText="Non"
              >
                <Button icon={<FlagOutlined />} style={{ color: '#c97a1a' }} />
              </Popconfirm>
            </Popover>
          )}
          {isAdmin && onDelete && (
            <Popover title="Supprimer" trigger="hover">
              <Popconfirm title="Êtes-vous sûr de vouloir supprimer?" onConfirm={() => onDelete(record.id_commande)} okText="Oui" cancelText="Non">
                <Button icon={<DeleteOutlined />} style={{ color: 'red' }} />
              </Popconfirm>
            </Popover>
          )}
        </Space>
      ),
    },
  ];
};

/**
 * Carte commande (mobile). handlers : { onOpen, onClient, onProducts, onEdit, onDelete, isAdmin }
 */
export const renderCommandeCard = (record, handlers = {}) => {
  const { onOpen, onProducts, onEdit, onDelete, onCloturer, isAdmin, showMontant } = handlers;
  const s = livraisonStatutOf(record.livraison_statut, record);
  const phone = phoneOf(record);
  const mInfo = showMontant ? montantInfo(record) : null;
  const actions = [
    <NdoeActionButton key="o" size="sm" variant="ink" icon={<EyeOutlined />} onClick={() => onOpen && onOpen(record.id_commande)}>Ouvrir</NdoeActionButton>,
    onProducts && <NdoeActionButton key="p" size="sm" variant="secondary" icon={<PlusCircleOutlined />} onClick={() => onProducts(record.id_commande)}>Produits</NdoeActionButton>,
    phone && <NdoeActionButton key="w" size="sm" variant="secondary" icon={<WhatsAppOutlined />} as="a" href={`https://wa.me/${String(phone).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer">WhatsApp</NdoeActionButton>,
    onEdit && <NdoeActionButton key="e" size="sm" variant="ghost" icon={<EditOutlined />} onClick={() => onEdit(record.id_commande)}>Éditer</NdoeActionButton>,
    onCloturer && record.nom_statut !== 'Annulée' && (
      <Popconfirm key="c" title="Clôturer cette commande ?" description="Paires restantes → retour magasin. Sans aucune vente → Annulée."
        onConfirm={() => onCloturer(record.id_commande)} okText="Clôturer" cancelText="Non">
        <NdoeActionButton size="sm" variant="secondary" icon={<FlagOutlined />}>Clôturer</NdoeActionButton>
      </Popconfirm>
    ),
    isAdmin && onDelete && (
      <Popconfirm key="d" title="Supprimer cette commande ?" onConfirm={() => onDelete(record.id_commande)} okText="Oui" cancelText="Non">
        <NdoeActionButton size="sm" variant="danger" icon={<DeleteOutlined />}>Suppr.</NdoeActionButton>
      </Popconfirm>
    ),
  ].filter(Boolean);

  return (
    <NdoeDataCard
      title={record.nom}
      subtitle={`${formatCommandeCode(record)}${record.date_commande ? ' · ' + format(new Date(record.date_commande), 'dd-MM-yyyy') : ''}`}
      badge={<NdoeStatusBadge kind="custom" tone={s.ndoe} label={s.label} />}
      metrics={[
        { value: record.nom_statut || '—', label: 'statut' },
        ...(mInfo ? [{
          value: mInfo.partiel ? `${money(mInfo.montant)} / ${money(mInfo.attendu)}` : money(mInfo.montant),
          label: mInfo.confirme ? 'validé' : (mInfo.partiel ? 'partiel' : 'attendu'),
        }] : []),
        { value: record.id_shop === 1 ? 'Ngaliema' : (record.id_shop ?? '—'), label: 'shop' },
      ]}
      onClick={() => onOpen && onOpen(record.id_commande)}
      actions={actions}
    />
  );
};
